// Client-side utilities to keep verification/KYC/card uploads under production
// ingress body limits (~1 MB). Images are re-encoded to JPEG with dimension
// and quality caps; non-image files are allowed only if small enough.
// Active settings come from the per-company uploadSettingsStore (admin-tunable);
// fallback defaults are used if the store isn't loaded yet.

import { getActiveUploadSettings } from '../stores/uploadSettingsStore';

const DEFAULT_MAX_DIMENSION = 1600;
const DEFAULT_JPEG_QUALITY = 0.8;
const DEFAULT_MAX_SIZE_KB = 900;
// Allow a small buffer above the target before we hard-fail (final base64 size).
const HARD_BUFFER_BYTES = 300 * 1024;

const resolveSettings = () => {
  const s = getActiveUploadSettings() || {};
  const maxDimension = Number(s.maxDimension) || DEFAULT_MAX_DIMENSION;
  const jpegQuality = Number(s.jpegQuality) || DEFAULT_JPEG_QUALITY;
  const maxSizeKb = Number(s.maxSizeKb) || DEFAULT_MAX_SIZE_KB;
  return {
    maxDimension,
    jpegQuality,
    maxDataUrlBytes: maxSizeKb * 1024,
    hardFailBytes: maxSizeKb * 1024 + HARD_BUFFER_BYTES,
  };
};

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const compressImage = async (file, { maxDim, quality, maxDataUrlBytes } = {}) => {
  const srcUrl = await readAsDataUrl(file);
  const img = await loadImage(srcUrl);
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const ratio = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  // Iterate quality down until under target size (min quality 0.4).
  let q = quality;
  let dataUrl = canvas.toDataURL('image/jpeg', q);
  while (dataUrl.length > maxDataUrlBytes && q > 0.4) {
    q -= 0.1;
    dataUrl = canvas.toDataURL('image/jpeg', q);
  }
  return dataUrl;
};

/**
 * Prepare a file for JSON upload as a base64 data URL.
 * - Images: compressed to JPEG within size limits.
 * - PDFs: accepted only if under the hard cap.
 * - Other types: rejected with a clear error.
 *
 * Returns { dataUrl, originalBytes, finalBytes, wasCompressed }.
 * Throws Error with a user-friendly message when the file can't be uploaded.
 */
export const prepareUploadDataUrl = async (file, { allowPdf = false } = {}) => {
  if (!file) throw new Error('No file selected');
  const type = (file.type || '').toLowerCase();
  const originalBytes = file.size || 0;
  const { maxDimension, jpegQuality, maxDataUrlBytes, hardFailBytes } = resolveSettings();

  if (type.startsWith('image/')) {
    let compressed;
    try {
      compressed = await compressImage(file, {
        maxDim: maxDimension,
        quality: jpegQuality,
        maxDataUrlBytes,
      });
    } catch {
      throw new Error('Could not process the selected image. Please try a different file.');
    }
    const finalBytes = Math.round(compressed.length * 0.75);
    if (compressed.length > hardFailBytes) {
      throw new Error('Image is too large even after compression. Please upload a smaller screenshot.');
    }
    return {
      dataUrl: compressed,
      originalBytes,
      finalBytes,
      wasCompressed: finalBytes < originalBytes,
    };
  }

  if (type === 'application/pdf') {
    if (!allowPdf) {
      throw new Error('Only image files are allowed here. Please upload a screenshot (JPG/PNG).');
    }
    const dataUrl = await readAsDataUrl(file);
    if (dataUrl.length > hardFailBytes) {
      throw new Error('PDF is too large. Please upload a smaller file.');
    }
    return { dataUrl, originalBytes, finalBytes: originalBytes, wasCompressed: false };
  }

  throw new Error('Unsupported file type. Please upload a JPG or PNG image.');
};

const KB = 1024;
const MB = KB * 1024;

export const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '0 KB';
  if (bytes >= MB) return `${(bytes / MB).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / KB))} KB`;
};
